import { Component } from '@theme/component';
import {
  debounce,
  isDesktopBreakpoint,
  onDocumentLoaded,
  requestIdleCallback,
  setHeaderMenuStyle,
} from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    onDocumentLoaded(this.#scheduleIdlePrefetch);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Axome - safety nets: without them nothing guarantees the panel (and the
    // underlay sized by --full-open-header-height) goes back to its closed state
    // when the pointer never crosses the list item boundary (scroll, tab/window
    // switch) or when the user just wants out (Escape).
    document.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    window.addEventListener('blur', this.#forceClose);

    this.#recoverInitialHover();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointermove', this.#onInitialPointerMove);
    document.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    window.removeEventListener('blur', this.#forceClose);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#clearHoverTimers();
    this.#cleanupMutationObserver();
    this.#fetchAbortController?.abort();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleClose();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Hover intent delays, in milliseconds.
   *
   * `open` / `switch` stop a pointer that merely sweeps across the nav (typically
   * diagonally, on its way to the cart or to the panel of another entry) from
   * opening every panel it grazes. `close` gives the pointer time to travel from
   * the top-level link to the panel without the menu collapsing under it.
   *
   * @type {{ open: number, switch: number, close: number }}
   */
  #delays = {
    open: 60,
    switch: 120,
    close: 120,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #openTimer;

  /**
   * Item the pending open is armed for. Both the `<li>` and its `<a>` bind
   * `activate`, so without this the timer would be restarted on the second one
   * and the panel would take twice as long to appear.
   * @type {HTMLElement | null}
   */
  #pendingItem = null;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeTimer;

  /**
   * Scroll position when the current panel was opened, and how far the page has
   * to move before the panel is considered stale. See `#onScroll`.
   * @type {number}
   */
  #scrollAtOpen = 0;

  /** @type {number} */
  #scrollCloseThreshold = 24;

  /**
   * Static flags shared across instances — guarantee a single fetch per page
   * regardless of which top-level entry was hovered first.
   * @type {boolean}
   */
  static #fetched = false;
  /** @type {boolean} */
  static #fetching = false;

  /**
   * @type {AbortController | null}
   */
  #fetchAbortController = null;

  /**
   * Section ID exposed by the Shopify section wrapper (`id="shopify-section-XXX"`).
   * @returns {string | null}
   */
  get sectionId() {
    const wrapper = this.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  }

  /**
   * Cancel a pending open.
   */
  #cancelOpen() {
    clearTimeout(this.#openTimer);
    this.#openTimer = undefined;
    this.#pendingItem = null;
  }

  /**
   * Cancel a pending close.
   */
  #cancelClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  #clearHoverTimers() {
    this.#cancelOpen();
    this.#cancelClose();
  }

  /**
   * Arm the close grace period. Re-entering the active item cancels it.
   */
  #scheduleClose() {
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#deactivate();
    }, this.#delays.close);
  }

  /**
   * The panel is anchored to the header, so it must not survive a page scroll:
   * the pointer ends up somewhere else without any boundary event being fired.
   *
   * Only a real displacement counts, and only against an already open panel:
   * trackpad inertia keeps firing `scroll` for about a second after the gesture,
   * and reacting to those would repeatedly kill a pending open — the menu would
   * feel like it refuses to follow the cursor. Panel-internal scrolling doesn't
   * reach `document`, so it is not affected either way.
   */
  #onScroll = () => {
    if (!this.#state.activeItem) return;
    if (Math.abs(window.scrollY - this.#scrollAtOpen) < this.#scrollCloseThreshold) return;

    this.#forceClose();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const focused = document.activeElement;
    this.#forceClose();
    // Drop focus too — a focused link would keep the nav styled as open
    // (:focus-within) and re-arm `activate` on the next focus event.
    if (focused instanceof HTMLElement && this.contains(focused)) focused.blur();
  };

  #onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') this.#forceClose();
  };

  /**
   * Unconditionally return the header to its closed state. Unlike `#deactivate`
   * it doesn't depend on `#state.activeItem` being in sync with the DOM, so it
   * also recovers from a panel left open by a missed `pointerleave` — including
   * the header underlay, whose height is driven by `--full-open-header-height`.
   *
   * `data-active` is deliberately left in place: it drives `content-visibility`,
   * and clearing it would make the browser drop the panel's layout and rebuild it
   * on the next hover — which stutters and gives `offsetHeight` the intrinsic
   * placeholder size instead of the real one.
   */
  #forceClose = () => {
    this.#clearHoverTimers();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    this.dataset.overflowExpanded = 'false';
    this.style.setProperty('--submenu-opacity', '0');
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.headerComponent?.style.setProperty('--full-open-header-height', '0px');

    for (const expanded of this.querySelectorAll('[aria-expanded="true"]')) {
      expanded.setAttribute('aria-expanded', 'false');
    }
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Arm the activation of the hovered menu item.
   *
   * The opening is deferred by `#delays.open` / `#delays.switch` so a pointer
   * that only sweeps across the nav never opens a panel: leaving the item before
   * the delay elapses cancels the pending open (see `deactivate`). Focus opens
   * straight away — keyboard navigation has no accidental hover.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#fetchMenuContent();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    // Coming back to the item that is already open: just call off the closing.
    if (item == this.#state.activeItem) {
      this.#cancelOpen();
      this.#cancelClose();
      return;
    }

    // Already armed for this very item (the <li> and its <a> both bind activate).
    if (this.#pendingItem === item) return;

    const isDefaultSlot = event.target.slot === '';
    const delay =
      event instanceof FocusEvent ? 0 : this.#state.activeItem ? this.#delays.switch : this.#delays.open;

    this.#cancelOpen();
    this.#pendingItem = item;
    this.#openTimer = setTimeout(() => {
      this.#openTimer = undefined;
      this.#pendingItem = null;
      if (!item.isConnected) return;
      this.#cancelClose();
      this.#open(item, isDefaultSlot);
    }, delay);
  };

  /**
   * Open the panel of a menu item and size the header accordingly.
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot - Whether the item lives in the default slot (not the overflow menu)
   */
  #open(item, isDefaultSlot) {
    if (!this.headerComponent) return;

    this.#scrollAtOpen = window.scrollY;
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Hydrate lazy images now that the panel is visible (observer-CFH).
      // Required because IntersectionObserver doesn't always re-fire when a parent
      // transitions from display:none / visibility:hidden to visible.
      document.dispatchEvent(
        new CustomEvent('reactivateLazyObserver', {
          detail: { lazyParentElement: submenu },
        })
      );

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Arm the deactivation of the active item.
   *
   * Also cancels any pending open, so an item merely crossed on the way
   * somewhere else never gets to open its panel.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    this.#cancelOpen();

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    // Axome - keep open when pointer/focus moves into the submenu
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      this.#cancelClose();
      return;
    }

    // Axome - grace period: the pointer needs time to travel from the top-level
    // link down to the panel, and a fast diagonal move often exits the item for
    // a few frames before landing where the user aimed.
    if (this.#state.activeItem) this.#scheduleClose();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being
    // hovered — retry once the grace period has elapsed again, otherwise nothing
    // would ever close the panel after the pointer finally leaves.
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) {
      this.#scheduleClose();
      return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Browsers don't dispatch `pointerenter` on page load even if the cursor is
   * already inside an element — so the placeholder loader stays forever when
   * a user reloads with the mouse over the menu. Check `:hover` after mount,
   * and if nothing matches yet (CSS `:hover` is only refreshed by a pointer
   * event, not by the initial layout) listen for `pointermove` and re-check
   * until we land on a menu item, then re-dispatch `pointerenter`.
   */
  #recoverInitialHover = () => {
    requestAnimationFrame(() => {
      if (this.#triggerHoveredItem()) return;
      document.addEventListener('pointermove', this.#onInitialPointerMove);
    });
  };

  #onInitialPointerMove = () => {
    if (this.#triggerHoveredItem()) {
      document.removeEventListener('pointermove', this.#onInitialPointerMove);
    }
  };

  /**
   * @returns {boolean} true if a hovered menu item was found and pointerenter dispatched.
   */
  #triggerHoveredItem() {
    const hovered = /** @type {HTMLElement | null} */ (this.querySelector('.menu-list__list-item:hover'));
    if (!hovered) return false;
    hovered.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    return true;
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Warm the mega-menu fetch on idle after load (desktop only), so the first
   * hover doesn't wait on Section Rendering.
   */
  #scheduleIdlePrefetch = () => {
    if (!isDesktopBreakpoint()) return;

    const connection = /** @type {{ saveData?: boolean, effectiveType?: string } | undefined} */ (
      /** @type {any} */ (navigator).connection
    );
    if (connection?.saveData) return;
    if (connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;

    requestIdleCallback(() => {
      if (!this.isConnected) return;
      this.#fetchMenuContent({ priority: 'low' });
    });
  };

  /**
   * Fetch the full mega menu content via Section Rendering API on first hover
   * (or earlier via idle prefetch). Idempotent across the whole page via the
   * static flag.
   * @param {{ priority?: 'low' | 'high' | 'auto' }} [options]
   */
  async #fetchMenuContent({ priority } = {}) {
    if (HeaderMenu.#fetched || HeaderMenu.#fetching) return;
    if (!this.sectionId) return;
    if (!this.querySelector('.mega-menu__content--placeholder')) return;

    HeaderMenu.#fetching = true;
    this.#fetchAbortController = new AbortController();

    const is404 = /** @type {any} */ (window).routes?.template_name === '404';
    const path = is404 ? '/' : window.location.pathname;
    const url = `${path}?section_id=${this.sectionId}&lazy=1`;

    try {
      const response = await fetch(url, {
        signal: this.#fetchAbortController.signal,
        // @ts-ignore — Fetch Priority API not in lib.dom yet.
        priority,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.#injectMegaMenuContent(doc);
      HeaderMenu.#fetched = true;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[header-menu] mega menu fetch failed:', error);
    } finally {
      HeaderMenu.#fetching = false;
    }
  }

  /**
   * Inject fetched panel content into placeholders, matching by `data-menu-handle`.
   * Also adopts `<style data-section-stylesheet>` from the response into <head>,
   * since Shopify no longer bundles {% stylesheet %} blocks for snippets that
   * weren't rendered at pageload (announced 2026, breaks Section Rendering API
   * consumers that don't re-extract the styles).
   * @param {Document} doc - Parsed response from `?section_id=header`
   */
  #injectMegaMenuContent(doc) {
    if (!document.querySelector('style[data-mega-menu-stylesheet]')) {
      for (const style of doc.querySelectorAll('style[data-section-stylesheet]')) {
        const clone = /** @type {HTMLStyleElement} */ (style.cloneNode(true));
        clone.removeAttribute('data-section-stylesheet');
        clone.setAttribute('data-mega-menu-stylesheet', '');
        document.head.appendChild(clone);
      }
    }

    const incoming = doc.querySelectorAll('.mega-menu__content[data-menu-handle]');
    for (const newContent of incoming) {
      const handle = newContent.getAttribute('data-menu-handle');
      if (!handle) continue;
      const target = /** @type {HTMLElement | null} */ (
        this.querySelector(`.mega-menu__content[data-menu-handle="${CSS.escape(handle)}"]`)
      );
      if (!target) continue;
      target.innerHTML = newContent.innerHTML;
      target.removeAttribute('aria-busy');
      target.dataset.state = 'loaded';
      target.classList.remove('mega-menu__content--placeholder');
    }

    document.dispatchEvent(
      new CustomEvent('reactivateLazyObserver', { detail: { lazyParentElement: this } })
    );

    if (this.#state.activeItem) {
      this.#recalcActiveSubmenuHeight();
    }
  }

  /**
   * Recalculate the active submenu height after content injection. Uses double
   * `requestAnimationFrame` to ensure layout is committed before measuring.
   */
  #recalcActiveSubmenuHeight() {
    if (!this.#state.activeItem) return;
    const submenu = findSubmenu(this.#state.activeItem);
    if (!submenu) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const height = submenu.offsetHeight;
        if (height <= 0) return;
        this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
        this.#setFullOpenHeaderHeight(height);
      });
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  // `event.target` can be the link itself, a descendant of it (e.g. the title
  // <span>) or an ancestor (the <li>, when the pointer enters through the
  // gap-bridging ::after). `closest` resolves the link-or-descendant cases,
  // `querySelector` the ancestor case — so hovering the text opens the menu
  // exactly like hovering the gap below it.
  return element.closest('[ref="menuitem"]') ?? element.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
